(() => {
  if (customElements.get('octane-quiz')) {
    return;
  }

  class OctaneQuiz extends HTMLElement {
    constructor() {
      super();
      this.isBound = false;
      this.handleQuizCompletedBound = this.handleQuizCompleted.bind(this);
      this.handleRetakeClickBound = this.handleRetakeClick.bind(this);
      this.handleGoToProductPageBound = this.handleGoToProductPage.bind(this);
      this.onProtocolButtonClickBound = this.onProtocolButtonClick.bind(this);
      this.globalRetakeBound = false;
      this.protocolInterceptorBound = false;
      this.resultsUiRendered = false;
      this.lastQuizDetail = null;
    }

    // Wire Octane events and the protocol-button interceptor when the
    // custom element mounts. Theme editor preview only needs the blog scroller.
    connectedCallback() {
      if (this.isDesignMode()) {
        this.initializeBlogScroller(this);
        return;
      }

      if (OctaneQuiz.activeInstance && OctaneQuiz.activeInstance !== this) {
        return;
      }

      OctaneQuiz.activeInstance = this;

      // Octane's native results screen redirects to a product page via the
      // "Show me my protocol" button. Intercept it so the custom results page
      // can render in-place, regardless of which results mode is active.
      this.bindProtocolButtonInterceptor();

      if (this.isBound) {
        return;
      }

      document.addEventListener(
        'octane.quiz.completed',
        this.handleQuizCompletedBound,
        false,
      );
      document.addEventListener(
        'octane.quiz.accessed',
        this.handleQuizCompletedBound,
        false,
      );
      // Octane fires this when a results-page button with "Go to product page"
      // behaviour is pressed. It is the reliable hook for the "Show me my
      // protocol" button; cancelling it stops Octane's redirect.
      document.addEventListener(
        'octane.quiz.goToProductPage',
        this.handleGoToProductPageBound,
        true,
      );
      this.isBound = true;
    }

    // Tear down listeners so a re-mounted section does not double-bind.
    disconnectedCallback() {
      if (this.isBound) {
        document.removeEventListener(
          'octane.quiz.completed',
          this.handleQuizCompletedBound,
          false,
        );
        document.removeEventListener(
          'octane.quiz.accessed',
          this.handleQuizCompletedBound,
          false,
        );
        document.removeEventListener(
          'octane.quiz.goToProductPage',
          this.handleGoToProductPageBound,
          true,
        );
        this.isBound = false;
      }

      if (this.protocolInterceptorBound) {
        document.removeEventListener(
          'click',
          this.onProtocolButtonClickBound,
          true,
        );
        this.protocolInterceptorBound = false;
      }

      if (OctaneQuiz.activeInstance === this) {
        OctaneQuiz.activeInstance = null;
      }
    }

    // True while the Shopify theme editor is previewing this section.
    isDesignMode() {
      return document.documentElement.classList.contains('shopify-design-mode');
    }

    // `<template>` holding the custom results markup cloned on quiz completion.
    get templateElement() {
      return this.querySelector('[data-quiz-results-template]');
    }

    // Section Rendering key for the main recommended product (quiz-results-product).
    get productRendererSectionId() {
      return this.dataset.productRendererSectionId || '';
    }

    // Try underscore and hyphen variants of the primary product renderer section ID.
    get productRendererSectionIds() {
      const configuredId = this.productRendererSectionId.trim();
      const sectionIds = [
        configuredId,
        configuredId.replace(/_/g, '-'),
        configuredId.replace(/-/g, '_'),
        'quiz-results-product',
        'quiz_results_product',
      ].filter((id) => id !== '');
      return [...new Set(sectionIds)];
    }

    // Section Rendering key for supplementary card-product snippets.
    get supplementaryRendererSectionId() {
      return this.dataset.supplementaryRendererSectionId || 'quiz_supplementary_products';
    }

    // Shopify Section Rendering accepts either the template JSON key
    // (quiz_supplementary_products) or the liquid filename slug
    // (quiz-supplementary-products); try both plus common fallbacks.
    get supplementaryRendererSectionIds() {
      const configuredId = this.supplementaryRendererSectionId.trim();
      const sectionIds = [
        configuredId,
        configuredId.replace(/_/g, '-'),
        configuredId.replace(/-/g, '_'),
        'quiz-supplementary-products',
        'quiz_supplementary_products',
      ].filter((id) => id !== '');
      return [...new Set(sectionIds)];
    }

    // Heading above the supplementary product grid, from octane-quiz data attribute.
    get supplementaryTitle() {
      return this.dataset.supplementaryTitle || '';
    }

    // Merchant toggle from the Octane Quiz section settings.
    get showSupplementaryProducts() {
      return this.dataset.showSupplementaryProducts !== 'false';
    }

    // Octane product block ID for extras; empty means scan all sections.
    get supplementaryProductsBlockId() {
      return (this.dataset.supplementaryProductsBlockId || '').trim();
    }

    // Pill text when Octane returns no answer strings.
    get noAnswersText() {
      return this.dataset.noAnswersText || '';
    }

    // When true, defer custom results until "Show me my protocol" is clicked.
    get showOriginalResults() {
      return this.dataset.showOriginalResults === 'true';
    }

    // Strip Octane's `r` session token and reload so the quiz can restart clean.
    handleRetakeClick() {
      navigateToRetakeQuiz();
    }

    // Reveal the sticky retake control after custom results mount.
    showGlobalRetakeButton() {
      const retakeWrap = this.querySelector('[data-retake-quiz-wrap]');
      const retakeButton = this.querySelector('[data-retake-quiz-global]');

      if (!retakeWrap || !retakeButton) {
        return;
      }

      retakeWrap.hidden = false;

      if (this.globalRetakeBound) {
        return;
      }

      retakeButton.addEventListener('click', this.handleRetakeClickBound);
      this.globalRetakeBound = true;
    }

    // Unhide promo banners elsewhere on the page once results are shown.
    revealOfferBanners() {
      document
        .querySelectorAll('[data-quiz-results-banner]')
        .forEach((banner) => {
          banner.hidden = false;
        });
    }

    // Parse `/products/{handle}` from an Octane or Shopify product URL.
    getProductHandle(productLink) {
      if (!productLink || typeof productLink !== 'string') {
        return '';
      }

      const match = productLink.match(/\/products\/([^?#/]+)/i);
      return match && match[1] ? match[1] : '';
    }

    // First Octane results section that contains a non-empty products array.
    findPrimaryResultSection(resultSections) {
      if (!Array.isArray(resultSections)) {
        return null;
      }

      for (let index = 0; index < resultSections.length; index += 1) {
        const section = resultSections[index];
        if (
          section &&
          Array.isArray(section.products) &&
          section.products.length > 0
        ) {
          return section;
        }
      }

      return null;
    }

    // This quiz's results page uses HTML + button content blocks instead of
    // structured `sections[].products`. The recommended product URL is stored
    // on the results CTA (e.g. bottom_content[].url for content_type "button").
    findProductUrlInContentBlocks(contentBlocks) {
      if (!Array.isArray(contentBlocks)) {
        return '';
      }

      for (let index = 0; index < contentBlocks.length; index += 1) {
        const block = contentBlocks[index];
        if (!block || typeof block !== 'object') {
          continue;
        }

        if (typeof block.url === 'string' && this.getProductHandle(block.url)) {
          return block.url;
        }

        const nestedUrl = this.findProductUrlInContentBlocks([
          ...(Array.isArray(block.top_content) ? block.top_content : []),
          ...(Array.isArray(block.bottom_content) ? block.bottom_content : []),
        ]);
        if (nestedUrl) {
          return nestedUrl;
        }
      }

      return '';
    }

    // Resolve the Shopify handle for the main recommended product from
    // `sections[].products` or nested content-block button URLs.
    resolveProductHandle(detail) {
      if (!detail || typeof detail !== 'object') {
        return '';
      }

      const primarySection = this.findPrimaryResultSection(detail.sections);
      const primaryProduct =
        primarySection && primarySection.products
          ? primarySection.products[0]
          : null;
      const handleFromProduct = primaryProduct
        ? this.getProductHandle(primaryProduct.link)
        : '';
      if (handleFromProduct) {
        return handleFromProduct;
      }

      const contentSources = [
        ...(Array.isArray(detail.sections) ? detail.sections : []),
        ...(Array.isArray(detail.product_blocks) ? detail.product_blocks : []),
        ...(Array.isArray(detail.state?.info?.product_blocks)
          ? detail.state.info.product_blocks
          : []),
      ];

      for (let index = 0; index < contentSources.length; index += 1) {
        const source = contentSources[index];
        const productUrl = this.findProductUrlInContentBlocks([
          ...(Array.isArray(source?.top_content) ? source.top_content : []),
          ...(Array.isArray(source?.bottom_content)
            ? source.bottom_content
            : []),
        ]);
        const handle = this.getProductHandle(productUrl);
        if (handle) {
          return handle;
        }
      }

      return '';
    }

    // Octane block/section ID tied to the primary recommendation.
    resolvePrimaryProductsBlockId(detail) {
      const primarySection = this.findPrimaryResultSection(detail?.sections);
      if (primarySection?.id) {
        return String(primarySection.id);
      }

      const primaryProduct = primarySection?.products?.[0];
      if (primaryProduct?.product_block_id) {
        return String(primaryProduct.product_block_id);
      }

      return '';
    }

    // Gather unique products for a given Octane product block ID from
    // detail.sections, detail.product_blocks, and state.info.product_blocks.
    collectProductsForBlock(detail, blockId) {
      if (!blockId) {
        return [];
      }

      const products = [];
      const seenLinks = new Set();

      const addProduct = (product) => {
        if (!product || typeof product !== 'object') {
          return;
        }

        const link = product.link || product.full_url;
        if (!link || seenLinks.has(link)) {
          return;
        }

        seenLinks.add(link);
        products.push({ ...product, link });
      };

      const sections = Array.isArray(detail?.sections) ? detail.sections : [];

      for (let index = 0; index < sections.length; index += 1) {
        const resultSection = sections[index];
        if (!resultSection || !Array.isArray(resultSection.products)) {
          continue;
        }

        if (String(resultSection.id) === blockId) {
          resultSection.products.forEach(addProduct);
          continue;
        }

        resultSection.products.forEach((product) => {
          if (String(product?.product_block_id) === blockId) {
            addProduct(product);
          }
        });
      }

      const blockSources = [
        ...(Array.isArray(detail?.product_blocks) ? detail.product_blocks : []),
        ...(Array.isArray(detail?.state?.info?.product_blocks)
          ? detail.state.info.product_blocks
          : []),
      ];

      for (let index = 0; index < blockSources.length; index += 1) {
        const block = blockSources[index];

        if (Array.isArray(block)) {
          block.forEach((product) => {
            if (String(product?.product_block_id) === blockId) {
              addProduct(product);
            }
          });
          continue;
        }

        if (
          !block ||
          (String(block.product_block_id) !== blockId &&
            String(block.id) !== blockId)
        ) {
          continue;
        }

        if (Array.isArray(block.products)) {
          block.products.forEach(addProduct);
        }

        if (block.containers && typeof block.containers === 'object') {
          Object.values(block.containers).forEach((container) => {
            if (container?.data) {
              addProduct(container.data);
            }
          });
        }
      }

      return products;
    }

    // Convert Octane product objects to handles, skipping the primary and deduping.
    handlesFromProducts(products, primaryHandle, maxHandles = 2) {
      const handles = [];
      const seen = new Set();

      if (primaryHandle) {
        seen.add(primaryHandle);
      }

      for (let index = 0; index < products.length; index += 1) {
        const handle = this.getProductHandle(products[index]?.link);

        if (!handle || seen.has(handle)) {
          continue;
        }

        seen.add(handle);
        handles.push(handle);

        if (handles.length >= maxHandles) {
          break;
        }
      }

      return handles;
    }

    // Default supplementary source: walk every section's products array.
    resolveExtraProductHandlesFromAllSections(detail, primaryHandle) {
      const handles = [];
      const seen = new Set();

      if (primaryHandle) {
        seen.add(primaryHandle);
      }

      const sections = Array.isArray(detail?.sections) ? detail.sections : [];

      for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex += 1) {
        const resultSection = sections[sectionIndex];
        if (!resultSection || !Array.isArray(resultSection.products)) {
          continue;
        }

        for (
          let productIndex = 0;
          productIndex < resultSection.products.length;
          productIndex += 1
        ) {
          const product = resultSection.products[productIndex];
          const handle = this.getProductHandle(product?.link);

          if (!handle || seen.has(handle)) {
            continue;
          }

          seen.add(handle);
          handles.push(handle);

          if (handles.length >= 2) {
            return handles;
          }
        }
      }

      return handles;
    }

    // Octane multi-product quizzes return recommendations grouped by product
    // block ID on detail.sections[].products. When a block ID is configured,
    // read extras from that block first, then fall back to the primary block.
    resolveExtraProductHandles(detail) {
      const primaryHandle = this.resolveProductHandle(detail);
      const configuredBlockId = this.supplementaryProductsBlockId;

      if (!configuredBlockId) {
        return this.resolveExtraProductHandlesFromAllSections(detail, primaryHandle);
      }

      const primaryBlockId = this.resolvePrimaryProductsBlockId(detail);
      const blockIdsToTry = [configuredBlockId];

      if (primaryBlockId && primaryBlockId !== configuredBlockId) {
        blockIdsToTry.push(primaryBlockId);
      }

      for (let index = 0; index < blockIdsToTry.length; index += 1) {
        const products = this.collectProductsForBlock(detail, blockIdsToTry[index]);
        const handles = this.handlesFromProducts(products, primaryHandle, 2);

        if (handles.length > 0) {
          return handles;
        }
      }

      return [];
    }

    // Renders card-product snippets via the quiz-supplementary-products
    // section (?product_handle=&product_handle_2=). Returns only the inner
    // card markup so it can be injected into [data-supplementary-cards]
    // without duplicating the grid wrapper from the results template.
    async fetchRenderedSupplementaryProducts(handles) {
      if (!Array.isArray(handles) || handles.length === 0) {
        return '';
      }

      const sectionIds = this.supplementaryRendererSectionIds;
      let lastStatus = null;

      for (let index = 0; index < sectionIds.length; index += 1) {
        const query = new URLSearchParams();
        query.set('section_id', sectionIds[index]);
        query.set('product_handle', handles[0] || '');

        if (handles[1]) {
          query.set('product_handle_2', handles[1]);
        }

        query.set('_', String(Date.now()));

        const url = `${window.location.pathname}?${query.toString()}`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
        });

        if (!response.ok) {
          lastStatus = response.status;
          continue;
        }

        const responseText = await response.text();
        const parsed = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );
        const cardsContainer = parsed.querySelector(
          '.quiz-supplementary-products-source__cards',
        );

        if (cardsContainer) {
          return cardsContainer.innerHTML;
        }

        const source = parsed.querySelector('.shopify-section');
        return source ? source.innerHTML : responseText;
      }

      throw new Error(
        `Supplementary products section render request failed with status ${lastStatus || 'unknown'} for section IDs: ${sectionIds.join(', ')}`,
      );
    }

    // Initialise Swiper on the "Learn more" article carousel after results mount.
    initializeBlogScroller(container, attempt = 0) {
      const scroller = container.querySelector('[data-blog-scroller]');
      const prevButton = container.querySelector('[data-blog-prev]');
      const nextButton = container.querySelector('[data-blog-next]');

      if (!scroller || !prevButton || !nextButton) {
        return;
      }

      if (typeof Swiper === 'undefined') {
        if (attempt < 10) {
          window.setTimeout(() => {
            this.initializeBlogScroller(container, attempt + 1);
          }, 120);
        }
        return;
      }

      if (scroller.swiper) {
        scroller.swiper.destroy(true, true);
      }

      // Keep this slider deterministic across dynamic result re-renders.
      this.blogSwiper = new Swiper(scroller, {
        slidesPerView: 1.15,
        spaceBetween: 16,
        navigation: {
          nextEl: nextButton,
          prevEl: prevButton,
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
          },
          990: {
            slidesPerView: 4,
          },
        },
      });
    }

    // Minimal title/price/link UI when Section Rendering for the primary product fails.
    renderFallbackProduct(slot, product) {
      if (!slot || !product) {
        return;
      }

      slot.innerHTML = '';

      const wrapper = document.createElement('div');
      wrapper.className = 'quiz-results__fallback-product';

      const titleNode = document.createElement('p');
      titleNode.className = 'quiz-results__fallback-product-title h4';
      titleNode.textContent = product.title || '';
      wrapper.appendChild(titleNode);

      const priceNode = document.createElement('p');
      priceNode.className = 'quiz-results__fallback-product-price p2';
      priceNode.textContent = product.price_formatted || '';
      wrapper.appendChild(priceNode);

      if (product.link) {
        const link = document.createElement('a');
        link.className = 'button button--primary button--small';
        link.href = product.link;
        link.textContent = 'View product';
        wrapper.appendChild(link);
      }

      slot.appendChild(wrapper);
    }

    // Fetch quiz-results-product HTML via Shopify Section Rendering API.
    async fetchRenderedProduct(productHandle) {
      if (!productHandle) {
        return '';
      }

      const sectionIds = this.productRendererSectionIds;
      let lastStatus = null;

      for (let index = 0; index < sectionIds.length; index += 1) {
        const query = new URLSearchParams();
        query.set('section_id', sectionIds[index]);
        query.set('product_handle', productHandle);
        query.set('_', String(Date.now()));

        const url = `${window.location.pathname}?${query.toString()}`;
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'same-origin',
        });

        if (!response.ok) {
          lastStatus = response.status;
          continue;
        }

        const responseText = await response.text();
        const parsed = new DOMParser().parseFromString(
          responseText,
          'text/html',
        );
        const source = parsed.querySelector('.shopify-section');
        return source ? source.innerHTML : responseText;
      }

      throw new Error(
        `Product section render request failed with status ${lastStatus || 'unknown'} for section IDs: ${sectionIds.join(', ')}`,
      );
    }

    // innerHTML does not execute <script> tags; re-insert them so product-form
    // and quiz-card-atc custom elements initialise after dynamic injection.
    reloadInsertedElHTML(el) {
      el.querySelectorAll('script').forEach((oldScriptTag) => {
        const newScriptTag = document.createElement('script');
        Array.from(oldScriptTag.attributes).forEach((attribute) => {
          newScriptTag.setAttribute(attribute.name, attribute.value);
        });
        newScriptTag.appendChild(
          document.createTextNode(oldScriptTag.innerHTML),
        );
        oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
      });
    }

    // Capture clicks on Octane's "Show me my protocol" CTA in capture phase.
    bindProtocolButtonInterceptor() {
      if (this.protocolInterceptorBound) {
        return;
      }

      document.addEventListener('click', this.onProtocolButtonClickBound, true);
      this.protocolInterceptorBound = true;
    }

    // Click fallback when showOriginalResults is on and the protocol CTA is used.
    onProtocolButtonClick(event) {
      // Only the results-stage CTA should be intercepted. The Octane intro
      // "start" button shares the primary class but lives in a different
      // content section and fires before the quiz reports a result.
      const primaryButton = event.target.closest('.oct-quiz-btn--primary');
      if (!primaryButton) {
        return;
      }

      const protocolWrapper = primaryButton.closest(
        '[data-content-section="bottom_content"]',
      );
      if (!protocolWrapper) {
        return;
      }

      if (this.resultsUiRendered || !this.lastQuizDetail) {
        return;
      }

      // Stop Octane from redirecting to the recommended product page so the
      // custom results render in place on the quiz page.
      event.preventDefault();
      event.stopImmediatePropagation();

      this.renderResults(this.lastQuizDetail);
    }

    // Octane event handler for results CTAs that would redirect to a product page.
    handleGoToProductPage(event) {
      if (this.resultsUiRendered) {
        return;
      }

      // Cancel Octane's redirect to the recommended product page (the event is
      // dispatched as cancelable before Octane navigates) and render the custom
      // results in place instead.
      if (typeof event.preventDefault === 'function') {
        event.preventDefault();
      }
      if (typeof event.stopImmediatePropagation === 'function') {
        event.stopImmediatePropagation();
      }

      const detail = this.lastQuizDetail || (event && event.detail) || {};
      this.renderResults(detail);
    }

    // Entry point for octane.quiz.completed and octane.quiz.accessed.
    async handleQuizCompleted(event) {
      const detail = event && event.detail ? event.detail : {};
      this.lastQuizDetail = detail;

      window.igEvents = window.igEvents || [];
      window.igEvents.push({ event: 'QuizCompleted' });

      // When the native Octane results screen is shown, defer rendering until
      // the "Show me my protocol" button is pressed (handled by the click
      // interceptor) instead of replacing it immediately.
      if (this.showOriginalResults) {
        return;
      }

      await this.renderResults(detail);
    }

    // Clone the results template, hydrate primary + supplementary products,
    // inject into the quiz embed, and scroll the shopper back to the top.
    async renderResults(detail) {
      detail = detail || {};
      const template = this.templateElement;
      const quizWrapper =
        detail.quiz_wrapper_element ||
        this.querySelector('[data-quiz-embed]') ||
        this;

      if (this.resultsUiRendered) {
        return;
      }

      if (!quizWrapper || !template || !template.content) {
        return;
      }

      const clone = template.content.cloneNode(true);
      const answersContainer = clone.querySelector('[data-answers-container]');
      const aiMessageNode = clone.querySelector('[data-ai-message]');
      const productSlot = clone.querySelector('[data-product-slot]');
      const supplementarySlot = clone.querySelector('[data-supplementary-slot]');
      const supplementaryCards = clone.querySelector('[data-supplementary-cards]');
      // Scope to the slot — do not use [data-supplementary-title] globally
      // because the same attribute name is on <octane-quiz> for JS config.
      const supplementaryTitle = clone.querySelector(
        '[data-supplementary-slot] .quiz-results__supplementary-title',
      );

      const answers = detail.answers || {};
      const answerValues = Object.values(answers).filter(
        (value) => typeof value === 'string' && value.trim() !== '',
      );

      if (answersContainer) {
        if (answerValues.length > 0) {
          answerValues.forEach((answer) => {
            const pill = document.createElement('span');
            pill.className = 'quiz-results__answer-pill p3';
            pill.textContent = answer;
            answersContainer.appendChild(pill);
          });
        } else if (this.noAnswersText) {
          const fallback = document.createElement('span');
          fallback.className = 'quiz-results__answer-pill p3';
          fallback.textContent = this.noAnswersText;
          answersContainer.appendChild(fallback);
        }
      }

      if (aiMessageNode && detail.ai_personalized_message) {
        aiMessageNode.hidden = false;
        aiMessageNode.textContent = detail.ai_personalized_message;
      }

      const primarySection = this.findPrimaryResultSection(detail.sections);
      const primaryProduct =
        primarySection && primarySection.products
          ? primarySection.products[0]
          : null;
      const productHandle = this.resolveProductHandle(detail);

      if (productSlot && productHandle) {
        try {
          const renderedProduct =
            await this.fetchRenderedProduct(productHandle);
          if (renderedProduct && renderedProduct.trim() !== '') {
            productSlot.innerHTML = renderedProduct;
            this.reloadInsertedElHTML(productSlot);
            window?.okeWidgetApi?.initAllWidgets();
          } else if (primaryProduct) {
            this.renderFallbackProduct(productSlot, primaryProduct);
          }
        } catch (error) {
          console.error('Quiz results product render failed', error);
          if (primaryProduct) {
            this.renderFallbackProduct(productSlot, primaryProduct);
          }
        }
      } else if (productSlot && primaryProduct) {
        this.renderFallbackProduct(productSlot, primaryProduct);
      }

      // Supplementary cards sit directly under the primary product card.
      // Leave [data-supplementary-slot] hidden when Octane sends no extras
      // or the section render fails — no empty heading/grid shown.
      const extraHandles = this.resolveExtraProductHandles(detail);

      if (this.showSupplementaryProducts && supplementaryCards && extraHandles.length > 0) {
        try {
          const renderedSupplementary =
            await this.fetchRenderedSupplementaryProducts(extraHandles);

          if (renderedSupplementary && renderedSupplementary.trim() !== '') {
            supplementaryCards.innerHTML = renderedSupplementary;
            this.reloadInsertedElHTML(supplementaryCards);

            // Title comes from Liquid in the template; sync from the
            // octane-quiz data attribute when the merchant setting is present.
            if (supplementaryTitle && this.supplementaryTitle) {
              supplementaryTitle.textContent = this.supplementaryTitle;
            }

            if (supplementarySlot) {
              supplementarySlot.hidden = false;
            }
          }
        } catch (error) {
          console.error('Quiz supplementary products render failed', error);
        }
      }

      quizWrapper.classList.add('oct-quiz-content--custom-results');
      quizWrapper.insertBefore(clone, quizWrapper.firstChild);
      this.resultsUiRendered = true;
      this.showGlobalRetakeButton();
      this.revealOfferBanners();

      // The user reaches this point deep down the page (Octane's results screen
      // is tall); jump back to the top so the recommended product is seen first.
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });

      const mountedRoot = quizWrapper.querySelector('.quiz-results');
      if (mountedRoot) {
        this.initializeBlogScroller(mountedRoot);

        const retakeButton = mountedRoot.querySelector('[data-retake-quiz]');
        if (retakeButton) {
          retakeButton.addEventListener(
            'click',
            () => {
              navigateToRetakeQuiz();
            },
            { once: true },
          );
        }
      }
    }
  }

  OctaneQuiz.activeInstance = null;
  customElements.define('octane-quiz', OctaneQuiz);

  // Remove Octane result-session params from the URL and restart the quiz.
  function navigateToRetakeQuiz() {
    const url = new URL(window.location.href);
    url.searchParams.delete('r');

    if (url.hash.length > 1) {
      const raw = url.hash.slice(1);
      if (raw.includes('r=')) {
        const hp = new URLSearchParams(raw);
        hp.delete('r');
        const next = hp.toString();
        url.hash = next ? `#${next}` : '';
      }
    }

    const nextUrl = `${url.pathname}${url.search}${url.hash}`;
    const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

    if (nextUrl === currentUrl) {
      window.location.reload();
      return;
    }

    window.location.replace(nextUrl);
  }

  /**
   * Octane shareable result URLs carry a session token (often as #r=... per Octane docs,
   * sometimes ?r=... depending on landing URL). The quiz embed starts hidden until
   * "Start Quiz" runs; deep links must unhide it so Octane can hydrate and emit
   * octane.quiz.accessed with the same detail shape as octane.quiz.completed.
   */
  function pageLooksLikeOctaneResultShareLink() {
    return /[?&#]r=/.test(
      `${window.location.pathname}${window.location.search}${window.location.hash}`,
    );
  }

  // Shared result links (#r= / ?r=) must unhide the embed before Octane hydrates.
  function prepareQuizEmbedForSharedResultLink() {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }
    if (!pageLooksLikeOctaneResultShareLink()) {
      return;
    }

    const quizEmbed = document.querySelector('[data-quiz-embed]');
    const startSection = document.querySelector('[data-octane-quiz-start]');

    if (quizEmbed) {
      quizEmbed.hidden = false;
      quizEmbed.style.visibility = '';
      quizEmbed.style.position = '';
    }
    if (startSection) {
      startSection.hidden = true;
    }
  }

  // "Start Quiz" bypasses Octane's intro by auto-clicking their primary button.
  function initQuizStart() {
    if (document.documentElement.classList.contains('shopify-design-mode')) {
      return;
    }

    const startBtn = document.querySelector('[data-start-quiz-btn]');
    if (!startBtn) {
      return;
    }

    startBtn.addEventListener('click', (event) => {
      event.preventDefault();

      startBtn.classList.add('loading');
      startBtn.setAttribute('aria-busy', 'true');

      const startSection = document.querySelector('[data-octane-quiz-start]');
      const quizEmbed = document.querySelector('[data-quiz-embed]');

      // Un-hide embed so Octane can render, but keep it visually offscreen
      // so the user never sees Octane's own intro screen.
      if (quizEmbed) {
        quizEmbed.hidden = false;
        quizEmbed.style.visibility = 'hidden';
        quizEmbed.style.position = 'absolute';
      }

      // Poll for Octane's intro/start button and click it to advance past their screen
      let attempts = 0;
      const maxAttempts = 50;
      const interval = window.setInterval(() => {
        const octaneStartBtn = document.querySelector('.oct-quiz-btn--primary');
        if (octaneStartBtn) {
          window.clearInterval(interval);
          octaneStartBtn.click();
        } else {
          attempts += 1;
          if (attempts >= maxAttempts) {
            window.clearInterval(interval);
          }
        }
      }, 100);

      // Once the first question has loaded: hide the start section and reveal the quiz
      document.addEventListener(
        'octane.quiz.questionLoaded',
        () => {
          if (startSection) {
            startSection.hidden = true;
          }
          if (quizEmbed) {
            quizEmbed.style.visibility = '';
            quizEmbed.style.position = '';
          }
        },
        { once: true },
      );
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    prepareQuizEmbedForSharedResultLink();
    initQuizStart();
  });
})();
